import type { EdgeType } from "./edge-types";

// Identities are a big deal in the lineage graph. These need to be stable across rebuilds and storage.
// We use URNs to provide a stable, unique, and readable identifier for each resource and edge.
// Node Format: ws:[workspaceId]::[type]:[resourceId]
// Edge Format: ws:[workspaceId]::[nodeIdentityUrn(from)]>[edgeType]>[nodeIdentityUrn(to)]
//
// Examples
// ws:123::audience:1234567890
// ws:123::model_column:1234567890/email
// ws:123::audience:1234567890>uses_column>ws:123::model_column:1234567890/email

export const RESOURCE_TYPES = [
  "model",
  "model_column",
  "audience",
  "audience_template",
  "trait",
  "trait_template",
  "sync",
  "sync_template",
  "destination",
  "destination_rule",
  "journey",
  "journey_context_variable",
  "subset",
  "subset_category",
  "campaign",
  "idr_graph",
  "event_source",
  "model_relationship",
  "message",
  "decision_engine_flow",
  "decision_engine_feature_matrix",
  "decision_engine_outcome",
  "decision_engine_send_event",
  "decision_engine_channel",
] as const;

export type ResourceType = (typeof RESOURCE_TYPES)[number];

export const RESOURCE_KINDS = {
  model: ["parent", "related", "retl", "event", "realtimeEvent"],
  model_column: ["schema", "retl"],
  message: ["email", "sms", "push"],
} as const satisfies Partial<Record<ResourceType, readonly string[]>>;

export type ResourceKind<Type extends keyof typeof RESOURCE_KINDS> =
  (typeof RESOURCE_KINDS)[Type][number];

export type ResourceId = string & { readonly __brand: "ResourceId" };
export type NodeId = string & { readonly __brand: "NodeId" };
export type EdgeId = string & { readonly __brand: "EdgeId" };

export type NodeRef<Type extends ResourceType = ResourceType> = {
  type: Type;
  resourceId: ResourceId;
};

// A node for types that are not known to the app.
// This is used when the graph requests a node that may have been removed.
export type AnyNodeRef = {
  type: string;
  resourceId: string;
};

export interface ResourceIdCodec<Key> {
  encode(key: Key): ResourceId;
  decode(resourceId: ResourceId | string): Key;
}

function encodePart(part: unknown): string {
  if (typeof part !== "string") {
    throw new Error("resource ID parts must be a string");
  }
  if (part.length === 0) {
    throw new Error("resource ID parts must not be empty");
  }
  return encodeURIComponent(part);
}

function decodePart(part: string, label: string): string {
  try {
    const decoded = decodeURIComponent(part);
    if (decoded.length === 0) {
      throw new Error("empty part");
    }
    return decoded;
  } catch {
    throw new Error(`invalid ${label} resource ID`);
  }
}

export const singleResourceIdCodec: ResourceIdCodec<string> = {
  encode(key) {
    return encodePart(key) as ResourceId;
  },
  decode(resourceId) {
    return decodePart(resourceId, "single");
  },
};

export function compositeResourceIdCodec(
  partCount: number,
): ResourceIdCodec<readonly string[]> {
  if (!Number.isInteger(partCount) || partCount < 2) {
    throw new Error("composite resource IDs require at least 2 parts");
  }

  return {
    encode(key) {
      if (!Array.isArray(key) || key.length !== partCount) {
        throw new Error(`composite resource ID expected ${partCount} parts`);
      }
      return key.map(encodePart).join("/") as ResourceId;
    },
    decode(resourceId) {
      const parts = resourceId.split("/");
      if (parts.length !== partCount) {
        throw new Error(`composite resource ID expected ${partCount} parts`);
      }
      return parts.map((part) => decodePart(part, "composite"));
    },
  };
}

export function isResourceType(value: unknown): value is ResourceType {
  return (
    typeof value === "string" &&
    (RESOURCE_TYPES as readonly string[]).includes(value)
  );
}

export function createNodeRef<Type extends ResourceType>({
  type,
  resourceId,
}: NodeRef<Type>): NodeRef<Type> {
  if (!isResourceType(type)) {
    throw new Error(`unknown resource type: ${String(type)}`);
  }
  if (typeof resourceId !== "string" || resourceId.length === 0) {
    throw new Error("resource ID must not be empty");
  }
  return { type, resourceId };
}

// Format: [type]:[resourceId]
function nodeIdentityUrn(ref: AnyNodeRef): string {
  return `${ref.type}:${ref.resourceId}`;
}

// Format: ws:[workspaceId]::[type]:[resourceId]
export function createNodeId({
  workspaceId,
  ref,
}: {
  workspaceId: number;
  ref: AnyNodeRef;
}): NodeId {
  return `ws:${workspaceId}::${nodeIdentityUrn(ref)}` as NodeId;
}

// Format: ws:[workspaceId]::[nodeIdentityUrn(from)]>[edgeType]>[nodeIdentityUrn(to)]
export function createEdgeId({
  workspaceId,
  from,
  to,
  edgeType,
}: {
  workspaceId: number;
  from: NodeRef;
  to: NodeRef;
  edgeType: EdgeType;
}): EdgeId {
  return `ws:${workspaceId}::${nodeIdentityUrn(from)}>${edgeType}>${nodeIdentityUrn(to)}` as EdgeId;
}
